import { dlog } from '../utils/debug';
import { useParams } from 'react-router-dom';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getContrastTextColor } from '../utils/contrast';
import { detectFavoriteTeam } from '../utils/teamDetection';
import { inferMyTeam, getMostUsedColor } from '../utils/emotionEngine';
import { calculateSetsWon } from '../utils/matchCompletion';
import AnimatedScore from '../components/AnimatedScore';
import CurrentSet from '../components/CurrentSet';
import SetsWon from '../components/SetsWon';
import Sets from '../components/Sets';
import TeamPanel from '../components/TeamPanel';
import { DynamicFlapperUnifiedLogo } from '../components';
import { useNavigate } from 'react-router-dom';
import { ref, set, get, update, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import QRCode from 'react-qr-code';
import { CaretLeft } from 'phosphor-react';
import { useMatchFollowing } from '../hooks/useMatchFollowing';
import SignInSheet from '../components/SignInSheet';
import GeoBanner from '../components/GeoBanner';
import MatchCompletionMoment from '../components/MatchCompletionMoment';
import { isFeatureEnabled } from '../config/features';
import { queryGeoPermission, getCurrentLocation, computeVenueId } from '../utils/geo';
import { useWriteViewerPresence, useViewerCount } from '../utils/presence';
import { Eye } from 'phosphor-react';

import "../styles/MatchView.css";

function MatchEditor({
  hydratedData = null,
  viewerSet,
  setViewerSet,
  setMatchState,
  manualViewerSet,
  setManualViewerSet,
  matchId: matchIdProp,
  viewerScore,
  viewerSetsWon,
  readOnly: readOnlyProp,
  isDataLoading = false,
}) {
  const routeParams = useParams();
  const matchId = routeParams.matchId || matchIdProp;

  const { user } = useAuth();
  const toast = useToast();
  const [userSettings, setUserSettings] = useState(null);
  
  // State to track if user is creator (for editor access)
  const [isCreator, setIsCreator] = useState(false);
  const [creatorLoaded, setCreatorLoaded] = useState(false);

  // Load user notification settings
  useEffect(() => {
    if (!user) return;

    const loadSettings = async () => {
      try {
        const settingsRef = ref(db, `users/${user.uid}/settings`);
        const snapshot = await get(settingsRef);
        if (snapshot.exists()) {
          setUserSettings(snapshot.val());
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadSettings();
  }, [user]);

  // Auto-follow this match for notifications
  // Temporarily disabled to fix match creation issue
  // useMatchFollowing(matchId, user, userSettings);
  
  // Determine readOnly: use prop if provided, otherwise check if user is creator
  const readOnly = readOnlyProp !== undefined ? readOnlyProp : !isCreator;

  const navigate = useNavigate();

  // Default settings (will be loaded from Firebase for new matches)
  const [teamAColor, setTeamAColor] = useState('#AE1F24');
  const [teamBColor, setTeamBColor] = useState('#1E96A7');
  const [teamAScore, setTeamAScore] = useState(0);
  const [teamBScore, setTeamBScore] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [isSwitchingSets, setIsSwitchingSets] = useState(false);
  const [teamASetsWon, setTeamASetsWon] = useState(0);
  const [teamBSetsWon, setTeamBSetsWon] = useState(0);

  const [teamAName, setTeamAName] = useState('Team A');
  const teamARef = useRef(null);
  const [teamBName, setTeamBName] = useState("");
  const [allSetScores, setAllSetScores] = useState([]);
  // Save-prompt sheet shown after FINAL for anonymous users — gives
  // them a path to convert at the moment they have something to lose.
  const [showSavePromptSheet, setShowSavePromptSheet] = useState(false);
  const savePromptsEnabled = isFeatureEnabled('SAVE_PROMPTS', user);
  // Feature flags for geo features
  const geoBannerEnabled = isFeatureEnabled('GEO_BANNER', user);
  const venueTaggingEnabled = isFeatureEnabled('MATCH_VENUE_TAGGING', user);
  // Match completion moment: full-screen takeover after FINAL with
  // logo reaction, score breakdown, and share CTA. State holds the
  // finalized match snapshot so the moment can render after the save
  // call returns. Null = not showing.
  const completionMomentEnabled = isFeatureEnabled('MATCH_COMPLETION_MOMENT', user);
  const [completionMomentMatch, setCompletionMomentMatch] = useState(null);
  // Match's venue (lat/lng + coarse venueId). Captured opportunistically
  // if the creator has already granted geolocation permission. Held in
  // local state and folded into every saveMatch() write — saveMatch
  // uses set() (full-replace) so we can't store this independently.
  // Hydrated from the existing match record on load so it survives
  // re-edits in a session where permission isn't re-granted.
  const [matchVenue, setMatchVenue] = useState(null);
  // Guards the one-shot permission lookup so it doesn't re-fire on
  // every render that touches geo state.
  const venueLookupAttemptedRef = useRef(false);
  // Live viewer-count feature flag.
  const viewerPresenceEnabled = isFeatureEnabled('VIEWER_PRESENCE', user);

  // Writes presence to RTDB when this client is in viewer mode
  // (readOnly). onDisconnect cleans up on tab close; heartbeats keep
  // lastSeen fresh. Hook is always called per React rules; the
  // `enabled` flag short-circuits the effect when not in viewer mode.
  useWriteViewerPresence({
    matchId,
    user,
    enabled: viewerPresenceEnabled && readOnly && !!user,
  });

  // Reads the live count of non-stale viewers on this match, used
  // for the scorer-side "N watching" indicator. Excludes the
  // current user's own presence (defensive — they don't write it
  // when scoring anyway).
  const viewerCount = useViewerCount({
    matchId,
    excludeUid: user?.uid,
    enabled: viewerPresenceEnabled && !readOnly && !!matchId,
  });

  // Load user's default settings for new matches (when match doesn't exist yet)
  useEffect(() => {
    if (!user || !matchId) return;
    
    const loadUserSettingsForNewMatch = async () => {
      try {
        // First check if match exists
        const matchRef = ref(db, `matches/${matchId}`);
        const matchSnapshot = await get(matchRef);
        
        // Only load default settings if match doesn't exist (new match)
        if (!matchSnapshot.exists()) {
          const settingsRef = ref(db, `users/${user.uid}/settings`);
          const settingsSnapshot = await get(settingsRef);
          
          if (settingsSnapshot.exists()) {
            const settings = settingsSnapshot.val();
            if (settings.defaultTeamName) {
              setTeamAName(settings.defaultTeamName);
            }
            if (settings.defaultTeamColor) {
              setTeamAColor(settings.defaultTeamColor);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user settings for new match:', error);
      }
    };

    loadUserSettingsForNewMatch();
  }, [user, matchId]);

  function computeSetsWon(sets) {
    return sets.reduce(
      (acc, set) => {
        if (set.teamA > set.teamB) acc.teamA += 1;
        else if (set.teamB > set.teamA) acc.teamB += 1;
        return acc;
      },
      { teamA: 0, teamB: 0 }
    );
  }

  const teamBRef = useRef(null);

  const [hydrated, setHydrated] = useState(false);
  const createdAtRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const initialStatusRef = useRef(null);
  const [hasScoreChanges, setHasScoreChanges] = useState(false);
  const isInitialLoadRef = useRef(true);

  // 🔹 Modal state for QR sharing
  const [isShareOpen, setIsShareOpen] = useState(false);

  const viewerUrl = `${window.location.origin}/match/${matchId}`;

  // 🎯 Auto-detect favorite team function
  const checkForFavoriteTeam = useCallback(async () => {
    if (!user) return;

    try {
      // First check if auto-detection is enabled
      const settingsRef = ref(db, `users/${user.uid}/settings`);
      const settingsSnapshot = await get(settingsRef);
      const currentSettings = settingsSnapshot.exists() ? settingsSnapshot.val() : {};
      
      // If auto-detection is disabled, don't proceed
      if (currentSettings.autoDetectFavoriteTeam === false) {
        dlog('🚫 Auto-detect favorite team is disabled');
        return;
      }

      // Get all matches created by this user
      const matchesQuery = query(
        ref(db, 'matches'),
        orderByChild('creatorId'),
        equalTo(user.uid)
      );
      
      const snapshot = await get(matchesQuery);
      if (!snapshot.exists()) return;

      const matches = Object.entries(snapshot.val()).map(([id, data]) => ({
        id,
        ...data
      }));

      // Debug: Show all matches being analyzed
      dlog('🔍 All matches for detection:', matches.length);
      dlog('📊 Match details:', matches.map(m => ({ 
        id: m.id, 
        teamA: m.teamA?.name, 
        teamB: m.teamB?.name,
        createdAt: m.createdAt 
      })));

      // Use settings-specific threshold (more conservative than logo)
      const myTeam = inferMyTeam(matches, 'settings');
      const mostUsedColor = getMostUsedColor(matches, myTeam);
      
      dlog('🎯 Settings team detection (conservative threshold):', { myTeam, mostUsedColor });
      
      if (myTeam && mostUsedColor && myTeam !== 'Unknown') {
        // Find the original team name (not normalized) that matches myTeam
        let originalTeamName = myTeam; // fallback to normalized name
        
        // Look for the most recent match with this team to get the original name
        for (const match of matches.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))) {
          const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
          const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
          
          // Helper to normalize team names (same logic as emotionEngine)
          const normalizeTeamName = (teamName) => {
            if (!teamName) return teamName;
            return teamName
              .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
              .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
              .trim();
          };
          
          if (teamAName && normalizeTeamName(teamAName) === myTeam) {
            originalTeamName = teamAName;
            break;
          }
          if (teamBName && normalizeTeamName(teamBName) === myTeam) {
            originalTeamName = teamBName;
            break;
          }
        }
        
        dlog('🎯 Using original team name for settings:', { normalized: myTeam, original: originalTeamName });
        
        const currentDefaultTeam = `${currentSettings.defaultTeamName}|${currentSettings.defaultTeamColor}`;
        const detectedTeamKey = `${originalTeamName}|${mostUsedColor}`;
        
        // Only update if it's different from current default
        if (currentDefaultTeam !== detectedTeamKey) {
          await update(settingsRef, {
            defaultTeamName: originalTeamName, // Use original name, not normalized
            defaultTeamColor: mostUsedColor,
            favoriteTeamDetected: true,
            favoriteTeamDetectedAt: Date.now(),
            favoriteTeamUsageCount: matches.filter(m => {
              const teamAName = typeof m.teamA === 'object' ? m.teamA.name || m.teamA.teamName : m.teamA;
              const teamBName = typeof m.teamB === 'object' ? m.teamB.name || m.teamB.teamName : m.teamB;
              return teamAName === originalTeamName || teamBName === originalTeamName;
            }).length
          });
          
          dlog('✅ Updated default team settings to match flapper:', { originalTeamName, mostUsedColor });
          
          // Dispatch event to notify other components
          window.dispatchEvent(new CustomEvent('favoriteTeamDetected', {
            detail: { name: originalTeamName, color: mostUsedColor }
          }));
        }
      } else {
        // No clear favorite team detected - reset to defaults
        dlog('🔄 No clear favorite team detected, resetting to defaults');
        
        await update(settingsRef, {
          defaultTeamName: 'Team A',
          defaultTeamColor: '#AE1F24',
          favoriteTeamDetected: false,
          favoriteTeamDetectedAt: null,
          favoriteTeamUsageCount: 0
        });
        
        dlog('✅ Reset default team to defaults');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('favoriteTeamDetected', {
          detail: null // null indicates reset to defaults
        }));
      }
    } catch (error) {
      console.error('❌ Error detecting favorite team:', error);
    }
  }, [user]);

  // 🔹 Helper function to save match immediately
  const saveMatch = useCallback(() => {
    if (!user || !matchId || readOnly) {
      return Promise.resolve();
    }

    const matchData = {
      creatorId: user.uid,
      createdAt: createdAtRef.current || Date.now(),
      updatedAt: Date.now(),
      teamA: { name: teamAName || "Team A", color: teamAColor },
      teamB: { name: teamBName || "Team B", color: teamBColor },
      score: { teamA: teamAScore, teamB: teamBScore },
      setsWon: {
        teamA: teamASetsWon ?? 0,
        teamB: teamBSetsWon ?? 0
      },
      setNumber: currentSet,
      sets: allSetScores
    };

    // Handle status based on score changes
    if (hasScoreChanges) {
      // If score changes were made, set status to LIVE
      matchData.status = 'LIVE';
    } else if (initialStatusRef.current) {
      // If no score changes, keep initial status (could be 'final')
      matchData.status = initialStatusRef.current;
    }

    // Persist venue tag if captured. saveMatch uses set() (full
    // replace), so omitting these would drop them on every write.
    if (matchVenue && matchVenue.venueId) {
      matchData.venueId = matchVenue.venueId;
      matchData.location = {
        lat: matchVenue.lat,
        lng: matchVenue.lng,
        capturedAt: matchVenue.capturedAt,
      };
    }

    const matchRef = ref(db, `matches/${matchId}`);
    return set(matchRef, matchData)
      .then(() => {
        // 🎯 Auto-detect favorite team after saving
        checkForFavoriteTeam();
      })
      .catch(err => console.error("❌ Save error:", err));
  }, [user, matchId, readOnly, teamAName, teamBName, teamAColor, teamBColor, teamAScore, teamBScore, teamASetsWon, teamBSetsWon, currentSet, allSetScores, hasScoreChanges, matchVenue]);

  const handleCopyUrl = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(viewerUrl);
        toast.success("Match URL copied");
      } else {
        // Fallback for iOS/Safari
        const textArea = document.createElement("textarea");
        textArea.value = viewerUrl;
        textArea.style.position = "fixed"; // avoid scrolling to bottom
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
          toast.success("Match URL copied");
        } catch (err) {
          toast.error("Unable to copy URL — copy it manually: " + viewerUrl, 8000);
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error("Clipboard copy failed", err);
      toast.error("Unable to copy URL — copy it manually: " + viewerUrl, 8000);
    }
  };
  
  useEffect(() => {
    // If we have hydratedData (from MatchViewer), use it instead of loading from Firebase
    if (hydratedData) {
      setTeamAName(hydratedData.teamAName || "Team A");
      setTeamBName(hydratedData.teamBName || "Team B");
      setTeamAColor(hydratedData.teamAColor || "#AE1F24");
      setTeamBColor(hydratedData.teamBColor || "#1E96A7");
      setTeamAScore(hydratedData.teamAScore ?? 0);
      setTeamBScore(hydratedData.teamBScore ?? 0);
      setTeamASetsWon(hydratedData.teamASetsWon ?? 0);
      setTeamBSetsWon(hydratedData.teamBSetsWon ?? 0);
      setCurrentSet(hydratedData.currentSet || 1);
      setAllSetScores(hydratedData.allSetScores || [{ teamA: 0, teamB: 0 }]);
      
      // For viewers, they're never the creator
      setIsCreator(false);
      setCreatorLoaded(true);
      setHydrated(true);
      return;
    }

    // Otherwise load from Firebase (for editors)
    const matchRef = ref(db, `matches/${matchId}`);
    get(matchRef)
      .then(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.val();

          // Check if current user is the creator
          if (user && data.creatorId === user.uid) {
            setIsCreator(true);
          } else {
            setIsCreator(false);
          }
          setCreatorLoaded(true);

          setTeamAName(data.teamA?.name || "Team A");
          setTeamBName(data.teamB?.name || "Team B");
          setTeamAColor(data.teamA?.color || "#AE1F24");
          setTeamBColor(data.teamB?.color || "#1E96A7");

          setTeamAScore(data.score?.teamA ?? 0);
          setTeamBScore(data.score?.teamB ?? 0);

          setTeamASetsWon(data.setsWon?.teamA ?? 0);
          setTeamBSetsWon(data.setsWon?.teamB ?? 0);

          setCurrentSet(data.setNumber || 1);
          setAllSetScores(
            Array.isArray(data.sets) ? data.sets : [{ teamA: 0, teamB: 0 }]
          );

          // Store initial status to preserve it
          initialStatusRef.current = data.status || null;
          createdAtRef.current = data.createdAt || Date.now();

          // Preserve any existing venue tag so saveMatch's full-replace
          // write doesn't wipe it on subsequent edits. Recompute the
          // venueId from the stored lat/lng using current bucketing
          // logic — that way, when the bucket size changes (or we
          // refine the algorithm), existing matches self-heal on
          // their creator's next open. saveMatch will fire via the
          // matchVenue dep and persist the corrected value.
          if (data.venueId && data.location) {
            const refreshedId =
              computeVenueId(data.location.lat, data.location.lng) || data.venueId;
            setMatchVenue({
              venueId: refreshedId,
              lat: data.location.lat,
              lng: data.location.lng,
              capturedAt: data.location.capturedAt || data.createdAt || Date.now(),
            });
          }

          setHydrated(true);
        } else {
          console.warn("No match data found.");
          // If no match data exists, user is creating it, so they're the creator
          setIsCreator(true);
          setCreatorLoaded(true);
          setHydrated(true);
        }
      })
      .catch(err => {
        console.error("Error loading match:", err);
        setCreatorLoaded(true);
      });
  }, [matchId, user, hydratedData]);

  // Note: We no longer need a separate editors update since creatorId 
  // is set in the main match data and provides ownership

  // 🔹 Track score changes to determine if match should go back to LIVE
  useEffect(() => {
    if (!hydrated || readOnly) return;
    
    // Skip tracking changes during initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    
    // If any score/name changes, mark that changes have been made
    // Note: Don't include currentSet - that's just navigation, not a change
    setHasScoreChanges(true);
  }, [teamAScore, teamBScore, teamASetsWon, teamBSetsWon, teamAName, teamBName, teamAColor, teamBColor, allSetScores, hydrated, readOnly]);

  // 🔹 Auto-save with debounce
  useEffect(() => {
    if (!hydrated) return;

    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save after 300ms of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      saveMatch();
    }, 300);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [hydrated, saveMatch]);

  useEffect(() => {
    document.title = `${teamAName || "Team A"} vs ${teamBName || "Team B"}`;
  }, [teamAName, teamBName]);

  // Venue tagging: opportunistic — only fires if the creator has
  // already granted geolocation permission (e.g. via the GeoBanner on
  // landing). Stores the venue in local state so it folds into the
  // existing saveMatch() write path — no separate RTDB write needed,
  // which sidesteps the rules-validation race where partial updates
  // can't satisfy the "newData has creatorId" requirement before the
  // match record exists.
  useEffect(() => {
    if (!venueTaggingEnabled) return;
    if (venueLookupAttemptedRef.current) return;
    if (!hydrated || readOnly || !user || !isCreator) return;
    // Existing match already had a venue (hydrated) — keep it.
    if (matchVenue) {
      venueLookupAttemptedRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      const permission = await queryGeoPermission();
      if (cancelled) return;
      venueLookupAttemptedRef.current = true;
      if (permission !== 'granted') return;
      try {
        const loc = await getCurrentLocation();
        if (cancelled) return;
        const venueId = computeVenueId(loc.lat, loc.lng);
        if (!venueId) return;
        setMatchVenue({
          venueId,
          lat: loc.lat,
          lng: loc.lng,
          capturedAt: Date.now(),
        });
        dlog(`📍 Captured venue for match ${matchId}: ${venueId} — will persist on next save`);
      } catch (err) {
        // Silent — venue tagging is opportunistic, not load-bearing.
        dlog('Venue capture failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [venueTaggingEnabled, hydrated, readOnly, user, isCreator, matchId, matchVenue]);

  useEffect(() => {
    if (!readOnly || !matchId) return;

    const matchRef = ref(db, `matches/${matchId}`);
    const unsubscribe = onValue(matchRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();

        if (setMatchState) {
          setMatchState(prev => ({
            ...prev,
            teamAScore: data.score?.teamA ?? 0,
            teamBScore: data.score?.teamB ?? 0,
            currentSet: data.setNumber || 1,
            allSetScores: Array.isArray(data.sets) ? data.sets : [{ teamA: 0, teamB: 0 }],
            teamASetsWon: data.setsWon?.teamA ?? 0,
            teamBSetsWon: data.setsWon?.teamB ?? 0
          }));

          if (manualViewerSet === null) {
            setViewerSet(data.setNumber || 1);
          }
        }
        setTeamAName(data.teamA?.name || "Team A");
        setTeamBName(data.teamB?.name || "Team B");
        setTeamAColor(data.teamA?.color || "#AE1F24");
        setTeamBColor(data.teamB?.color || "#1E96A7");
        setTeamAScore(data.score?.teamA ?? 0);
        setTeamBScore(data.score?.teamB ?? 0);
        setTeamASetsWon(data.setsWon?.teamA ?? 0);
        setTeamBSetsWon(data.setsWon?.teamB ?? 0);
        setCurrentSet(data.setNumber || 1);
        setAllSetScores(Array.isArray(data.sets) ? data.sets : [{ teamA: 0, teamB: 0 }]);
      }
    }, (error) => {
      console.error("❌ Realtime sync error:", error);
    });

    return () => unsubscribe();
  }, [readOnly, matchId]);

  // Historic sets only: recalc sets-won
  useEffect(() => {
    if (!hydrated || readOnly) return;

    const isHistoricSet = currentSet < allSetScores.length;

    if (isHistoricSet) {
      const scoresToEvaluate = [...allSetScores];
      scoresToEvaluate[currentSet - 1] = { teamA: teamAScore, teamB: teamBScore };

      const computed = calculateSetsWon(scoresToEvaluate);

      setTeamASetsWon(computed.teamA);
      setTeamBSetsWon(computed.teamB);
    }
  }, [teamAScore, teamBScore, allSetScores, currentSet, hydrated, readOnly]);


  const handleBackClick = async () => {
    // Save match before navigating away
    await saveMatch();
    navigate('/');
  };

  const handleEndMatch = async () => {
    if (!matchId || readOnly) return;

    if (!window.confirm("Are you sure you want to finish this match?")) return;

    if (!user) {
      console.error("❌ No user signed in");
      return;
    }

    // Update sets with current scores
    const updatedScores = [...allSetScores];
    updatedScores[currentSet - 1] = { teamA: teamAScore, teamB: teamBScore };

    const finalSetsWon = updatedScores.reduce(
      (acc, set) => {
        if (set.teamA > set.teamB) acc.teamA += 1;
        else if (set.teamB > set.teamA) acc.teamB += 1;
        return acc;
      },
      { teamA: 0, teamB: 0 }
    );

    const matchRef = ref(db, `matches/${matchId}`);
    const finalData = {
      creatorId: user.uid,
      createdAt: createdAtRef.current || Date.now(),
      updatedAt: Date.now(),
      teamA: { name: teamAName || "Team A", color: teamAColor },
      teamB: { name: teamBName || "Team B", color: teamBColor },
      score: { teamA: teamAScore, teamB: teamBScore },
      setsWon: finalSetsWon,
      setNumber: currentSet,
      sets: updatedScores,
      status: "final"
    };
    if (matchVenue && matchVenue.venueId) {
      finalData.venueId = matchVenue.venueId;
      finalData.location = {
        lat: matchVenue.lat,
        lng: matchVenue.lng,
        capturedAt: matchVenue.capturedAt,
      };
    }
    // Save final match state
        set(matchRef, finalData)
      .then(() => {
        dlog("🏁 Match marked as final and saved");
        // Reset score changes flag since we're finalizing
        setHasScoreChanges(false);
        // Match completion moment: full-screen takeover with logo
        // reaction + share CTA. When the moment is dismissed, anon
        // users see the save prompt (Day 3); real users go home.
        // When the feature flag is off, preserve the original
        // immediate-dismissal behavior.
        if (completionMomentEnabled) {
          setCompletionMomentMatch({
            id: matchId,
            teamA: finalData.teamA,
            teamB: finalData.teamB,
            score: finalData.score,
            setsWon: finalData.setsWon,
            sets: finalData.sets,
            status: 'final',
          });
        } else if (savePromptsEnabled && user?.isAnonymous) {
          setShowSavePromptSheet(true);
        } else {
          navigate('/');
        }
      })
      .catch((err) => console.error("Error ending match:", err));
  };

  // Dismiss handler for the completion moment. Chains to the save
  // prompt for anon users, otherwise navigates home.
  const handleCompletionMomentDone = () => {
    setCompletionMomentMatch(null);
    if (savePromptsEnabled && user?.isAnonymous) {
      setShowSavePromptSheet(true);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="container">
      <div className="match-header">
        <button
          className="back-button"
          onClick={handleBackClick}
          title="Go back"
          aria-label="Go back"
        >
          <CaretLeft size={24} weight="bold" />
        </button>

        <div
          className="match-title"
          onClick={() => setIsShareOpen(true)}
          title="Share match"
        >
          <div className="qr-icon">
            {!hydrated || isDataLoading ? (
              <DynamicFlapperUnifiedLogo 
                matches={[]}
                size="small"
                isLoading={true}
                showTooltip={false}
              />
            ) : (
              <QRCode value={viewerUrl} size={36} />
            )}
          </div>
        </div>

        {!readOnly && viewerPresenceEnabled && viewerCount > 0 && (
          <div
            className="viewer-count"
            aria-label={`${viewerCount} ${viewerCount === 1 ? 'person' : 'people'} watching`}
            title="Live viewers"
          >
            <Eye size={16} weight="fill" aria-hidden="true" />
            <span className="viewer-count-num">{viewerCount}</span>
          </div>
        )}

        {!readOnly && (
          <button
            className="end-match-btn"
            onClick={handleEndMatch}
            title="Finish match"
            aria-label="Finish match"
          >
            FINISH
          </button>
        )}
      </div>

      {/* 🔹 Share Modal */}
      {isShareOpen && (
        <div
          className="share-modal"
          onClick={() => setIsShareOpen(false)}
        >
          <div
            className="share-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-btn"
              onClick={() => setIsShareOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            
            <div className="share-card-header">
              <h2 className="share-title">{teamAName || "Team A"} vs {teamBName || "Team B"}</h2>
              <p className="share-subtitle">Scan to follow the score</p>
            </div>
            
            <div className="share-qr-wrapper">
              <QRCode 
                value={viewerUrl} 
                size={200}
                style={{ padding: '1rem', background: 'white', borderRadius: '12px' }}
              />
            </div>
            
            <button
              className="share-copy-btn"
              onClick={handleCopyUrl}
            >
              Copy Link
            </button>
          </div>
        </div>
      )}

      {geoBannerEnabled && hydrated && !readOnly && (
        <GeoBanner currentUserUid={user?.uid} className="geo-banner--match-editor" />
      )}

      {hydrated && !isDataLoading && (
        <div className="match-view">
          <TeamPanel
          side="A"
          teamName={teamAName}
          setTeamName={readOnly ? undefined : setTeamAName}
          teamColor={teamAColor}
          setTeamColor={readOnly ? undefined : setTeamAColor}
          setTeamScore={readOnly ? undefined : setTeamAScore}
          teamScore={readOnly ? viewerScore?.teamA : teamAScore}
          setsWon={readOnly ? viewerSetsWon?.teamA : teamASetsWon}
          readOnly={readOnly}
          animate={!isSwitchingSets}
        />

        <Sets
          currentSet={readOnly ? viewerSet : currentSet}
          teamAScore={teamAScore}
          teamBScore={teamBScore}
          setCurrentSet={readOnly ? setViewerSet : setCurrentSet}
          setTeamAScore={readOnly ? undefined : setTeamAScore}
          setTeamBScore={readOnly ? undefined : setTeamBScore}
          setsWon={{ teamA: teamASetsWon, teamB: teamBSetsWon }}
          setSetsWon={
            readOnly
              ? undefined
              : ({ teamA = 0, teamB = 0 }) => {
                  setTeamASetsWon(teamA);
                  setTeamBSetsWon(teamB);
                }
          }
          allSetScores={allSetScores}
          setAllSetScores={readOnly ? undefined : setAllSetScores}
          hideIcon={readOnly}
          readOnly={readOnly}
          viewerSet={viewerSet}
          setViewerSet={setViewerSet}
          setMatchState={readOnly ? setMatchState : undefined}
          manualViewerSet={manualViewerSet}
          setManualViewerSet={setManualViewerSet}
          onSetSwitch={readOnly ? undefined : setIsSwitchingSets}
        />

        <TeamPanel
          side="B"
          teamName={teamBName}
          setTeamName={readOnly ? undefined : setTeamBName}
          teamColor={teamBColor}
          setTeamColor={readOnly ? undefined : setTeamBColor}
          setTeamScore={readOnly ? undefined : setTeamBScore}
          teamScore={readOnly ? viewerScore?.teamB : teamBScore}
          setsWon={readOnly ? viewerSetsWon?.teamB : teamBSetsWon}
          readOnly={readOnly}
          animate={!isSwitchingSets}
        />
      </div>
      )}

      {completionMomentMatch && (
        <MatchCompletionMoment
          match={completionMomentMatch}
          viewerUrl={viewerUrl}
          onDone={handleCompletionMomentDone}
        />
      )}

      <SignInSheet
        open={showSavePromptSheet}
        onClose={() => {
          setShowSavePromptSheet(false);
          navigate('/');
        }}
        title="Save your match"
        subtitle="Sign in to keep this match across devices. Your browser data could clear at any time."
      />
    </div>
  );
}

export default MatchEditor;
